import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
  Container,
  CardHeader,
  Button
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { baseUrl, uploadFile, apiPost } from "../../Utils";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import * as XLSX from 'xlsx';

// Interface for Form
interface FormValues {
  branch_name: string;
  address: string;
  email: string;
  phone: string;
  status: string;
  manager_name: string;
  manager_contact: string;
  manager_email: string;
  branch_image: FileList;
}

export default function AddBranch() {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormValues>();
  const navigate = useNavigate();

  const validatePhoneNumber = (value: string) => {
    if (!value) {
      return "Phone number is required.";
    }

    const sanitizedValue = value.replace(/\D/g, ""); // remove non-digits
    const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return "Invalid phone number.";
    }

    const allowedCountries: CountryCode[] = ["IN", "SA", "AE"];
    if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
      return "Only Indian, Saudi Arabian, or UAE numbers are allowed.";
    }

    return true;
  };

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      let branchImageName = "";
      if (data.branch_image && data.branch_image[0]) {
        branchImageName = await uploadFile(data.branch_image[0]);
      } else {
        // If required, this should have been caught by validation. 
        // But if explicit check needed:
        // toast.error("Please upload a branch image."); 
        // return;
      }

      const formData = new FormData();
      formData.append("branch_name", data.branch_name);
      formData.append("address", data.address);
      formData.append("email", data.email);
      formData.append("phone", data.phone);
      formData.append("status", data.status);
      formData.append("manager_name", data.manager_name || "");
      formData.append("manager_contact", data.manager_contact || "");
      formData.append("manager_email", data.manager_email || "");

      // Send the filename string instead of the file object
      if (branchImageName) {
        formData.append("branch_image", branchImageName);
      }

      await apiPost("branches/store", formData);

      toast.success("Branch added successfully", { autoClose: 1000 });
      reset();
      setTimeout(() => {
        navigate(`${process.env.PUBLIC_URL}/branch`);
      }, 1000);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "An unexpected error occurred";
      toast.error(errorMessage, { autoClose: 1000 });
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const downloadSampleExcel = () => {
    const headers = ["branch_name", "address", "email", "phone", "status", "manager_name", "manager_contact"];
    const rows = [["Main Branch", "123 Main St", "main@gmail.com", "+919876543210", "Active", "John Doe", "+919876543211"], ["Main Branchs", "123 Main Sts", "mains@gmail.com", "+919876543210", "Active", "John Doe", "+919876543211"]];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Branches");
    XLSX.writeFile(workbook, "branch_import_sample.xlsx");
  };

  const [isUploading, setIsUploading] = useState(false);

  const handleBulkImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setIsUploading(true);
      const file = e.target.files[0];

      const reader = new FileReader();
      reader.onload = async (evt) => {
        const arrayBuffer = evt.target?.result;
        const wb = XLSX.read(arrayBuffer, { type: 'array' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Client-side validation
        const validationErrors: string[] = [];
        if (data.length <= 1) {
          toast.error("File appears to be empty or contains only headers.");
          e.target.value = '';
          setIsUploading(false);
          return;
        }

        // Loop rows (skip header)
        for (let i = 1; i < data.length; i++) {
          const row = data[i];
          // Skip empty rows
          if (!row || row.length === 0) continue;

          // Check Branch Name (index 0)
          const branchName = row[0];
          if (branchName === undefined || branchName === null || branchName.toString().trim() === "") {
            validationErrors.push(`Row ${i + 1}: Branch Name is required`);
          }

          // Check Address (index 1)
          const address = row[1];
          if (address === undefined || address === null || address.toString().trim() === "") {
            validationErrors.push(`Row ${i + 1}: Address is required`);
          }

          // Check Email (index 2)
          const email = row[2];
          if (email === undefined || email === null || email.toString().trim() === "") {
            validationErrors.push(`Row ${i + 1}: Email is required`);
          }

          // Check Phone (index 3)
          const phone = row[3];
          if (phone === undefined || phone === null || phone.toString().trim() === "") {
            validationErrors.push(`Row ${i + 1}: Phone number is required`);
          }

          // Check Status (index 4)
          const status = row[4];
          if (status === undefined || status === null || status.toString().trim() === "") {
            validationErrors.push(`Row ${i + 1}: Status is required`);
          } else {
            const statusStr = status.toString().trim();
            if (statusStr !== 'Active' && statusStr !== 'Inactive') {
              validationErrors.push(`Row ${i + 1}: Status must be 'Active' or 'Inactive'`);
            }
          }

          // Check Manager Name (index 5)
          const managerName = row[5];
          if (managerName === undefined || managerName === null || managerName.toString().trim() === "") {
            validationErrors.push(`Row ${i + 1}: Manager Name is required`);
          }

          // Check Manager Contact (index 6)
          const managerContact = row[6];
          if (managerContact === undefined || managerContact === null || managerContact.toString().trim() === "") {
            validationErrors.push(`Row ${i + 1}: Manager Contact is required`);
          }
        }

        if (validationErrors.length > 0) {
          toast.error("Missing fields detected in the Excel file. Please check your data.");
          e.target.value = '';
          setIsUploading(false);
          return;
        }

        // Proceed to upload if client-side validation passes
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response: any = await apiPost("branches/import", formData);
          if (response.errors && response.errors.length > 0) {
            // Show backend errors as toasts
            response.errors.slice(0, 5).forEach((err: string) => toast.error(err));
            if (response.errors.length > 5) toast.warning(`Check console for ${response.errors.length - 5} more errors.`);
          } else {
            toast.success(response.message);
          }
          navigate(`${process.env.PUBLIC_URL}/branch`);
        } catch (error: any) {
          const errMsg = error.response?.data?.message || error.message || "Import failed";
          toast.error(errMsg);
          if (error.response?.data?.error) {
            toast.error(error.response.data.error);
          }
        } finally {
          e.target.value = '';
          setIsUploading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };


  return (
    <div className='page-body'>
      <Breadcrumbs
        pageTitle="Add Branch"
        parent="General"
        title="Add Branch"
      />
      <Container fluid>
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody className="custom-input form-validation">
              <Row className="mb-4">
                <Col md={8}>
                  <div className="mb-3">
                    <h5>Import Instructions:</h5>
                    <ul className="list-group list-group-flush small">
                      <li className="list-group-item py-1">1. Your Excel data should be in the format below. The first line of your Excel file should be the column headers as in the table example.</li>
                      <li className="list-group-item py-1">2. <strong>Status</strong> must be either <strong>Active</strong> or <strong>Inactive</strong>.</li>
                      <li className="list-group-item py-1">3. <strong>Phone</strong> numbers must include a valid country code. Only India (+91), UAE (+971), and Saudi Arabia (+966) numbers are allowed (e.g., +919876543210, +971516144983, +966597058094).</li>
                    </ul>
                  </div>
                </Col>

                <Col md={4} className="d-flex align-items-center justify-content-end gap-2">
                  <Button color="success" size="sm" onClick={downloadSampleExcel} outline>
                    Download Sample Excel
                  </Button>
                  <div style={{ position: 'relative', overflow: 'hidden', display: 'inline-block' }}>
                    <Button color="info" size="sm" outline disabled={isUploading}>
                      {isUploading ? "Uploading..." : "Upload Excel"}
                    </Button>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleBulkImport}
                      disabled={isUploading}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: isUploading ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Form onSubmit={handleSubmit(onSubmit)} noValidate >
                <Row className="g-3">
                  <Col md={6}>
                    <Label>Branch Name <span className="text-danger">*</span></Label>
                    <Controller
                      name="branch_name"
                      control={control}
                      rules={{ required: "Branch name is required" }}
                      render={({ field }) => (
                        <>
                          <Input
                            {...field}
                            placeholder="Enter Branch Name"
                            invalid={!!errors.branch_name}
                            style={{ borderColor: errors.branch_name ? "red" : "" }}
                            autoComplete="off"
                          />
                          <FormFeedback>
                            {errors.branch_name?.message}
                          </FormFeedback>
                        </>
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Label>Branch Address <span className="text-danger">*</span></Label>
                    <Controller
                      name="address"
                      control={control}
                      rules={{ required: "Address is required" }}
                      render={({ field }) => (
                        <>
                          <Input
                            type="textarea"
                            {...field}
                            placeholder="Enter Address"
                            invalid={!!errors.address}
                            style={{ borderColor: errors.address ? "red" : "" }}
                            rows={1}
                            autoComplete="off"
                          />
                          <FormFeedback>
                            {errors.address?.message}
                          </FormFeedback>
                        </>
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Label>Email <span className="text-danger">*</span></Label>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value:
                            /^[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/,
                          message:
                            "Invalid email address. Please enter a valid email.",
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <Input
                            type="email"
                            {...field}
                            placeholder="Enter Email"
                            invalid={!!errors.email}
                            style={{ borderColor: errors.email ? "red" : "" }}
                            autoComplete="none"
                          />
                          <FormFeedback>
                            {errors.email?.message}
                          </FormFeedback>
                        </>
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Label>Phone <span className="text-danger">*</span></Label>
                    <Controller
                      name="phone"
                      control={control}
                      rules={{
                        required: "Phone number is required",
                        validate: (value) => validatePhoneNumber(value),
                      }}
                      render={({ field }) => (
                        <>
                          <PhoneInput
                            country={"ae"}
                            onlyCountries={["ae", "sa", "in"]}
                            preferredCountries={["ae"]}
                            disableDropdown={false}
                            countryCodeEditable={false}
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            inputProps={{
                              name: "phone",
                              placeholder: "Enter phone number",
                              autoComplete: "none",
                            }}
                            inputStyle={{
                              backgroundColor: "var(--input-background-color)",
                              color: "var(--input-text-color)",
                              width: "100%",
                              borderColor: errors.phone
                                ? "red"
                                : "var(--input-border-color, #e0e0e0)",
                            }}
                            dropdownStyle={{
                              backgroundColor: "var(--input-background-color)",
                              backdropFilter: "blur(1000px)",
                            }}
                            buttonStyle={{
                              backgroundColor: "var(--input-background-color)",
                            }}
                            specialLabel=""
                          />
                          {errors.phone && (
                            <FormFeedback style={{ display: "block" }}>
                              {errors.phone.message}
                            </FormFeedback>
                          )}
                        </>
                      )}
                    />
                  </Col>

                  <Col xs={12}>
                    <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                      <CardHeader tag="h5" style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                      }}>
                        Manager Details
                      </CardHeader>
                      <CardBody>
                        <Row className="g-3">
                          <Col md={4}>
                            <Label>Manager Name <span className="text-danger">*</span></Label>
                            <Controller
                              name="manager_name"
                              control={control}
                              rules={{ required: "Manager Name is required" }}
                              render={({ field }) => (
                                <>
                                  <Input
                                    {...field}
                                    placeholder="Manager Name"
                                    invalid={!!errors.manager_name}
                                    style={{ borderColor: errors.manager_name ? "red" : "" }}
                                    autoComplete="none"
                                  />
                                  <FormFeedback>{errors.manager_name?.message}</FormFeedback>
                                </>
                              )}
                            />
                          </Col>
                          <Col md={4}>
                            <Label>Manager Contact <span className="text-danger">*</span></Label>
                            <Controller
                              name="manager_contact"
                              control={control}
                              rules={{
                                required: "Manager Contact is required",
                                validate: (value) => validatePhoneNumber(value),
                              }}
                              render={({ field }) => (
                                <>
                                  <PhoneInput
                                    country={"ae"}
                                    onlyCountries={["ae", "sa", "in"]}
                                    preferredCountries={["ae"]}
                                    disableDropdown={false}
                                    countryCodeEditable={false}
                                    value={field.value}
                                    onChange={(value) => field.onChange(value)}
                                    inputProps={{
                                      name: "manager_contact",
                                      placeholder: "Manager Contact",
                                      autoComplete: "none",
                                    }}
                                    inputStyle={{
                                      backgroundColor: "var(--input-background-color)",
                                      color: "var(--input-text-color)",
                                      width: "100%",
                                      borderColor: errors.manager_contact
                                        ? "red"
                                        : "var(--input-border-color, #e0e0e0)",
                                    }}
                                    dropdownStyle={{
                                      backgroundColor: "var(--input-background-color)",
                                      backdropFilter: "blur(1000px)",
                                    }}
                                    buttonStyle={{
                                      backgroundColor: "var(--input-background-color)",
                                    }}
                                    specialLabel=""
                                  />
                                  {errors.manager_contact && (
                                    <FormFeedback style={{ display: "block" }}>
                                      {errors.manager_contact.message}
                                    </FormFeedback>
                                  )}
                                </>
                              )}
                            />
                          </Col>
                          <Col md={4}>
                            <Label>Manager Email</Label>
                            <Controller
                              name="manager_email"
                              control={control}
                              rules={{
                                pattern: {
                                  value:
                                    /^[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/,
                                  message:
                                    "Invalid email address. Please enter a valid email.",
                                },
                              }}
                              render={({ field }) => (
                                <>
                                  <Input
                                    type="email"
                                    {...field}
                                    placeholder="Manager Email"
                                    invalid={!!errors.manager_email}
                                    style={{ borderColor: errors.manager_email ? "red" : "" }}
                                    autoComplete="none"
                                  />
                                  <FormFeedback>{errors.manager_email?.message}</FormFeedback>
                                </>
                              )}
                            />
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Label>Status <span className="text-danger">*</span></Label>
                    <Controller
                      name="status"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Status is required" }}
                      render={({ field }) => (
                        <>
                          <Input
                            type="select"
                            {...field}
                            invalid={!!errors.status}
                            style={{ borderColor: errors.status ? "red" : "" }}
                            autoComplete="none"
                          >
                            <option value="">Select Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </Input>
                          <FormFeedback>{errors.status?.message}</FormFeedback>
                        </>
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Label>Branch Image</Label>
                    <Controller
                      name="branch_image"
                      control={control}
                      rules={{
                        validate: (files: any) => {
                          if (files && files[0]) {
                            if (files[0].size > 2 * 1024 * 1024) {
                              return "File size must be less than 2MB";
                            }
                          }
                          return true;
                        }
                      }}
                      render={({ field: { value, onChange, ...field } }) => (
                        <>
                          <Input
                            type="file"
                            {...field}
                            onChange={(e) => {
                              onChange(e.target.files);
                            }}
                            invalid={!!errors.branch_image}
                            style={{ borderColor: errors.branch_image ? "red" : "" }}
                            accept="image/*"
                            autoComplete="none"
                          />
                          <FormFeedback>{errors.branch_image?.message}</FormFeedback>
                        </>
                      )}
                    />
                  </Col>

                  <Col xs={12} className="mt-2">
                    <Btn color="primary" disabled={isButtonDisabled}>
                      {isButtonDisabled ? <>Submiting <Spinner size="sm" /></> : "Submit"}
                    </Btn>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Container>
      <ToastContainer autoClose={1500} />
    </div>
  );
}
